/**
 * Putting server-side validation errors back on the fields that caused them.
 *
 * The API answers a rejected payload with:
 *
 * ```json
 * { "status": { "type": "validation_failed",
 *               "errors": [{ "property": "email", "messages": ["…"] }] } }
 * ```
 *
 * Dumping that into a banner above the form is the default, and it is a poor
 * one: the user has to read the banner, work out which field it means, and
 * scroll back to it. Attaching each message to its own field is the difference
 * between a form that corrects itself and one that argues with you.
 *
 * Client-side validation never removes the need for this. The server checks
 * things the client cannot know — whether an address is already registered,
 * whether a password appears in a breach list — so some errors can only ever
 * arrive from the server.
 */

import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

/** One field-level failure, in the shape the API sends. */
export interface ServerFieldError {
  property: string;
  messages: string[];
}

/** The subset of `ApiError` this module needs. Structural, so it can be
 *  tested without constructing a real one. */
export interface ServerErrorLike {
  fieldErrors?: ServerFieldError[];
  message: string;
  type?: string;
}

export interface ApplyResult {
  /** Fields that were matched to a form input. */
  applied: string[];
  /**
   * Messages with no matching field.
   *
   * Never silently dropped. A server complaining about a field the form does
   * not render still has to reach the user somehow, and losing it produces the
   * worst possible outcome: a form that refuses to submit and says nothing.
   */
  unmatched: string[];
}

/**
 * Attach server errors to form fields.
 *
 * @param error   The failure from the API.
 * @param setError `setError` from `useForm`.
 * @param known   The fields this form actually renders. Anything outside it is
 *                returned as unmatched rather than set, because
 *                `setError` on an unknown name silently does nothing.
 */
export function applyServerErrors<T extends FieldValues>(
  error: ServerErrorLike,
  setError: UseFormSetError<T>,
  known: readonly string[],
): ApplyResult {
  const applied: string[] = [];
  const unmatched: string[] = [];

  for (const fieldError of error.fieldErrors ?? []) {
    const messages = fieldError.messages.join(" ");
    if (known.includes(fieldError.property)) {
      setError(fieldError.property as Path<T>, {
        type: "server",
        message: messages,
      });
      applied.push(fieldError.property);
    } else {
      // Includes the property name: "That address is already registered" is
      // useful, but "email: that address is already registered" is useful even
      // when the field is not on screen.
      unmatched.push(`${fieldError.property}: ${messages}`);
    }
  }

  // A failure with no field errors at all — a rate limit, a conflict, a server
  // fault — still has a message the user must see.
  if (applied.length === 0 && unmatched.length === 0) {
    unmatched.push(error.message);
  }

  return { applied, unmatched };
}
